import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { changeSetting, TEXT_LOCALES, type PlayerSettings } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors, cssVariables, layout } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * Le impostazioni (03-SCHERMATE-TV §12): tre gruppi, e a destra la spiegazione della voce a fuoco.
 *
 * ## Il pannello che spiega non è un di più
 *
 * «Non un punto interrogativo da cercare, ma una descrizione che cambia mentre ci si muove.» Su un
 * televisore, a tre metri e con un telecomando, aprire un aiuto è un viaggio: chi non capisce una
 * voce la lascia com'è. Una descrizione che segue il fuoco costa uno sguardo, e trasforma
 * «Effetti visivi» da parola in decisione.
 *
 * ## Cosa fa e cosa non fa questa schermata
 *
 * Disegna, e chiama `onChange` con lo stato **nuovo**: le conseguenze — il preset che si spegne
 * quando si tocca una voce a mano — le decide `changeSetting` nel dominio. Se fossero qui
 * diventerebbero quattro `if` che al primo campo nuovo ne dimenticano uno.
 *
 * Le voci in sola lettura di «Questo dispositivo» — host, abbonamento, versione dell'app — sono
 * quelle che l'assistenza chiede per prime al telefono: stanno qui perché è dove le cerca chi sta
 * parlando col cliente, non perché servano a cambiare qualcosa.
 */

export interface SettingsScreenProps {
  readonly focusPort: FocusPort;
  readonly settings: PlayerSettings;
  readonly onChange: (next: PlayerSettings) => void;
  /** L'host del gateway e lo stato dell'abbonamento: sola lettura. */
  readonly host: string;
  readonly subscription: string;
  /**
   * Di che versione è questa copia dell'app.
   *
   * Sta qui perché [09](../../../../docs/09-DIAGNOSI-E-TELEMETRIA.md) lo chiede fra le voci di
   * questa schermata, e perché fuori dagli store **è l'unico posto da cui si può sapere**: nessun
   * elenco di app installate la mostra, e chi dà assistenza al telefono deve poterla far leggere.
   *
   * Chi la passa sa anche che può non saperla — il server di sviluppo e le prove non sono un
   * pacchetto installato — e in quel caso qui arriva un testo, non un numero finto.
   */
  readonly appVersion: string;
  /**
   * La versione nuova da proporre, o `null` se non ce n'è.
   *
   * Appare **solo quando c'è**: una riga fissa che dice «nessun aggiornamento» occuperebbe spazio
   * per dare una notizia che nessuno cerca, e la confonderebbe con la riga della versione installata
   * — due numeri vicini che sembrano in contraddizione.
   *
   * Fuori dagli store questo avviso non è una comodità: sui box Android non c'è nessuno store, e
   * **nessuno si aggiorna da solo**. Con il motore del browser impacchettato è anche l'unica via per
   * cui una correzione di sicurezza del motore arriva a un apparecchio
   * ([10](../../../../docs/10-DECISIONI-APERTE.md), D-1bis).
   */
  readonly updateVersion: string | null;
  readonly onExit: () => void;
  readonly onSignOut: () => void;
  /**
   * Quanti preferiti, e come arrivarci.
   *
   * Nel prototipo il gruppo «Contenuti» ha una riga sola — «Canali preferiti», con il conto a destra
   * — e porta alla griglia. Non è un doppione del menu: chi è nelle impostazioni per sistemare l'app
   * ci trova anche questa, e il conto gli dice se c'è qualcosa da sistemare.
   */
  readonly favoritesCount: number;
  readonly onOpenFavorites: () => void;
  /** Apre la pagina del controllo genitori: impostare, cambiare o disattivare il PIN. */
  readonly onOpenPin: () => void;
  /** Vero quando quattro cifre sono archiviate su questo apparecchio. */
  readonly pinSet: boolean;
}

/**
 * I gruppi del prototipo, nel suo ordine: prestazioni, contenuti, controllo genitori, account.
 *
 * Non sono decorazione. Un elenco di nove righe senza intestazioni obbliga a leggerle tutte per
 * trovarne una; e soprattutto mette «Esci» accanto a «Animazioni» come se fossero la stessa
 * categoria di cosa. Il raggruppamento è ciò che rende «Ambiente» trovabile senza leggere.
 */
type GroupId = 'performance' | 'content' | 'parental' | 'account';

/**
 * Il nome di ogni lingua **nella lingua stessa**, e per questo non passa dall'i18n.
 *
 * Chi cerca il turco cerca «Türkçe», non «Turco»: se i nomi si traducessero, un turco davanti a un
 * televisore in tedesco leggerebbe «Türkisch» e dovrebbe indovinarlo. Per la stessa ragione sono
 * identici in tutte e sette le tabelle, quindi tradurli sarebbe sette volte lo stesso lavoro per
 * peggiorare il risultato.
 *
 * Non si usa `Intl.DisplayNames`: non lo danno gli apparecchi dell'annata che reggiamo, e sarebbe
 * una funzione che su un televisore del 2019 lascia l'elenco vuoto.
 *
 * `auto` **sì** passa dall'i18n: «come il sistema» è una frase, non un nome proprio.
 */
export const LANGUAGE_NAMES: Readonly<Record<string, string>> = {
  en: 'English',
  it: 'Italiano',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pl: 'Polski',
  tr: 'Türkçe',
};

const GROUP_ORDER: ReadonlyArray<GroupId> = ['performance', 'content', 'parental', 'account'];

type Row =
  | { readonly kind: 'toggle'; readonly key: keyof PlayerSettings; readonly labelKey: string }
  | {
      readonly kind: 'choice';
      // Le tre voci che sono una scelta fra valori, non una levetta. Elencate per nome e non
      // `keyof PlayerSettings`: una scelta su un booleano non ha senso, e il compilatore lo dice qui
      // invece di lasciarla disegnare con una pastiglia vuota.
      readonly key: 'imageQuality' | 'guideHours' | 'locale';
      readonly labelKey: string;
      readonly values: ReadonlyArray<string | number>;
    }
  | { readonly kind: 'readonly'; readonly labelKey: string; readonly value: string }
  | { readonly kind: 'action'; readonly id: 'signOut' | 'favorites' | 'adultPin'; readonly labelKey: string };

/** A quale gruppo appartiene una riga. Sta accanto alla riga, non in un secondo elenco. */
type GroupedRow = Row & { readonly group: GroupId };

/**
 * Cosa fare quando si scorre oltre l'ultimo valore di una scelta.
 *
 * `stop` per le frecce, `wrap` per il clic e per OK — e la differenza non è una preferenza: dipende
 * da **se c'è una via di ritorno**.
 *
 * Sinistra e destra sono due direzioni, quindi fermarsi al bordo è giusto: un elenco di tre che
 * avvolge fa passare da «Alta» a «Bassa» con una freccia, cioè l'opposto di quel che si voleva.
 *
 * Il clic e OK hanno **una direzione sola**. Senza avvolgere, arrivati all'ultimo valore non si
 * torna più — e su un telefono, dove non ci sono frecce, la lingua restava bloccata su «Türkçe» per
 * sempre. Con sette lingue più «come il sistema» è un vicolo cieco a otto passi.
 */
type Edge = 'wrap' | 'stop';

export function SettingsScreen({
  focusPort,
  settings,
  onChange,
  host,
  subscription,
  appVersion,
  updateVersion,
  onExit,
  onSignOut,
  favoritesCount,
  onOpenFavorites,
  onOpenPin,
  pinSet,
}: SettingsScreenProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState(0);

  /*
    Un elenco solo, con il gruppo scritto accanto a ogni riga.

    La tentazione è quattro elenchi, uno per gruppo: si disegnano più facilmente e **si naviga molto
    peggio**, perché il cursore diventa due numeri — quale gruppo e quale riga — e ogni freccia deve
    sapere dove finisce un gruppo e comincia il successivo. Con un elenco piatto il cursore resta un
    numero e le intestazioni sono un fatto del disegno: si stampano quando il gruppo cambia.
  */
  const rows: ReadonlyArray<GroupedRow> = useMemo(() => {
    const declared: ReadonlyArray<GroupedRow> = [
      { group: 'performance', kind: 'toggle', key: 'resourceSaving', labelKey: 'settings.resourceSaving' },
      { group: 'performance', kind: 'toggle', key: 'animations', labelKey: 'settings.animations' },
      { group: 'performance', kind: 'toggle', key: 'visualEffects', labelKey: 'settings.visualEffects' },
      {
        group: 'performance',
        kind: 'choice',
        key: 'imageQuality',
        labelKey: 'settings.imageQuality',
        values: ['low', 'medium', 'high'],
      },
      { group: 'performance', kind: 'choice', key: 'guideHours', labelKey: 'settings.guideHours', values: [3, 6, 24] },
      { group: 'content', kind: 'action', id: 'favorites', labelKey: 'settings.favorites' },
      /*
        **Una voce che apre una schermata, non una levetta.**

        Era `{ kind: 'toggle', key: 'adultPinEnabled' }`: accendeva un booleano e nient'altro — non
        c'era nessun posto in cui scegliere le quattro cifre, quindi non c'era nessun PIN. Un
        controllo genitori acceso senza un PIN è la forma peggiore di funzione dichiarata e mai in
        vigore: se qualcosa avesse davvero letto quel booleano avrebbe bloccato del contenuto
        **senza nessun modo di sbloccarlo**.

        Adesso «attivo» significa che quattro cifre sono archiviate, e non che una levetta è alzata.
      */
      { group: 'parental', kind: 'action', id: 'adultPin', labelKey: 'settings.adultPin' },
      /*
        La lingua, in «Account e ambiente».

        Non in «Prestazioni», che è l'altro gruppo con delle scelte: la lingua è una proprietà di
        **questo apparecchio**, come l'host e la versione che stanno qui sotto. E sta in cima al
        gruppo perché è l'unica voce che si cambia: le altre tre sono in sola lettura e l'ultima è
        «Esci».
      */
      { group: 'account', kind: 'choice', key: 'locale', labelKey: 'settings.language', values: ['auto', ...TEXT_LOCALES] },
      { group: 'account', kind: 'readonly', labelKey: 'settings.host', value: host },
      { group: 'account', kind: 'readonly', labelKey: 'settings.subscription', value: subscription },
      { group: 'account', kind: 'readonly', labelKey: 'settings.appVersion', value: appVersion },
      ...(updateVersion === null
        ? []
        : [
            {
              group: 'account' as const,
              kind: 'readonly' as const,
              labelKey: 'settings.updateAvailable',
              value: updateVersion,
            },
          ]),
      { group: 'account', kind: 'action', id: 'signOut', labelKey: 'settings.signOut' },
    ];
    /*
      L'ordine lo decide `GROUP_ORDER`, non l'ordine in cui le righe sono scritte qui.

      L'intestazione si stampa quando il gruppo cambia: una riga aggiunta nel posto sbagliato
      spezzerebbe il suo gruppo in due e stamperebbe la stessa intestazione due volte, senza che
      niente protesti. Riordinando qui, quel difetto non è possibile — e chi aggiunge una riga può
      scriverla dove gli viene comodo.
    */
    return [...declared].sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
  }, [host, subscription, appVersion, updateVersion]);

  const onChangeRef = useRef(onChange);
  const onExitRef = useRef(onExit);
  const onSignOutRef = useRef(onSignOut);
  const onOpenFavoritesRef = useRef(onOpenFavorites);
  const onOpenPinRef = useRef(onOpenPin);
  useEffect(() => {
    onChangeRef.current = onChange;
    onExitRef.current = onExit;
    onSignOutRef.current = onSignOut;
    onOpenFavoritesRef.current = onOpenFavorites;
    onOpenPinRef.current = onOpenPin;
  }, [onChange, onExit, onSignOut, onOpenFavorites]);

  /**
   * Attiva una riga. Le sole in lettura non fanno niente, e non è un caso da gestire.
   *
   * **Quale riga è un parametro, non `cursor`.** Col telecomando coincidono sempre; col puntatore no,
   * e il gestore del clic faceva `setCursor(index)` e poi `activate()`. `setCursor` non cambia
   * `cursor` in questa resa — cambia quello della prossima — quindi `activate` leggeva ancora la riga
   * di **prima**: cliccando «Animazioni» si commutava «Risparmio risorse», che è la riga a fuoco. Con
   * il fuoco sulla prima voce, ogni clic sembrava non salvare niente, perché il cambiamento avveniva
   * altrove sullo schermo.
   */
  /*
    `edge` è il **secondo** argomento e non ha un predefinito, di proposito.

    Ogni chiamata dichiara se il bordo avvolge: è la differenza fra una lingua che si può riportare
    all'inizio e una che resta bloccata su «Türkçe», e un valore di scorta la renderebbe una
    distrazione invece di una decisione.

    E `at` sta per ultimo perché solo il clic lo passa: le altre due chiamate arrivano dal
    telecomando e usano il cursore. Averlo in mezzo costringeva a nominare `cursor` in una chiamata
    dentro l'effetto, cioè ad aggiungere una dipendenza che quell'effetto già ha attraverso
    `activate` — il lint lo segnalava, e aveva ragione.
  */
  const activate = useCallback(
    (direction: 1 | -1, edge: Edge, at: number = cursor) => {
      const row = rows[at];
      if (!row) return;
      if (row.kind === 'action') {
        if (row.id === 'favorites') onOpenFavoritesRef.current();
        else if (row.id === 'adultPin') onOpenPinRef.current();
        else onSignOutRef.current();
        return;
      }
      if (row.kind === 'toggle') {
        onChangeRef.current(changeSetting(settings, row.key, !settings[row.key] as never));
        return;
      }
      if (row.kind === 'choice') {
        const current = row.values.indexOf(settings[row.key] as string | number);
        const raw = current + direction;

        if (edge === 'stop' && (raw < 0 || raw >= row.values.length)) return;

        // Il modulo in due passi perché in JavaScript `-1 % 8` fa `-1` e non `7`: con il solo
        // operatore, tornare indietro dal primo valore darebbe un indice negativo e `values[-1]`
        // è `undefined` — cioè una scelta che si svuota invece di avvolgersi.
        const next = ((raw % row.values.length) + row.values.length) % row.values.length;
        onChangeRef.current(changeSetting(settings, row.key, row.values[next] as never));
      }
    },
    [rows, cursor, settings],
  );

  useEffect(() => {
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        onExitRef.current();
        return true;
      }
      if (key.kind === 'select') {
        // OK: una direzione sola, quindi in fondo si ricomincia. Vedi `Edge`.
        activate(1, 'wrap');
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'up') setCursor((c) => (c > 0 ? c - 1 : c));
      if (key.direction === 'down') setCursor((c) => (c < rows.length - 1 ? c + 1 : c));
      if (key.direction === 'left') activate(-1, 'stop');
      if (key.direction === 'right') activate(1, 'stop');
      return true;
    });
  }, [focusPort, rows.length, activate]);

  const focusedRow = rows[cursor];

  return (
    <div
      data-settings="1"
      style={{
        // Le misure del prototipo: 64 in alto, 72 a destra, 60 a sinistra, e 48 fra le due colonne.
        flex: 1,
        minWidth: 0,
        display: 'flex',
        gap: 48,
        padding: '64px 72px 0 60px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          // Larghezza fissa, non `flex: 1`: la scheda che spiega deve avere sempre lo stesso posto,
          // altrimenti si sposta quando una riga ha un valore lungo — e una descrizione che balla
          // mentre si scorre è peggio di nessuna descrizione.
          /*
            **Il respiro serve al contorno del fuoco, e non basta 12.**

            Fatti i conti: la riga è larga 876 (900 meno i 12+12 di respiro), a fuoco si ingrandisce
            dell'1,02 — cioè 4,4 px per lato — poi il contorno sta 4 px più in fuori ed è spesso 4.
            Totale **12,4 px oltre la riga**, contro 12 di respiro. E `overflow-y: auto` fa diventare
            `auto` anche l'orizzontale: quel che sborda non esce, viene **tagliato**. L'anello ambra
            risultava mozzato ai lati, che è il difetto che si vede senza saperlo misurare.

            Si allarga la colonna di 8 e si porta il respiro a 16, poi la si tira indietro di 4 con un
            margine negativo: le righe restano larghe 876 e cominciano allo stesso punto — 60 − 4 + 16
            = 72 — quindi **nessuna misura del prototipo cambia**, cambia solo lo spazio in cui il
            contorno può stare.

            In alto la stessa cosa in piccolo: 8 px non bastavano agli 8,8 dell'anello, e la riga in
            cima si tagliava scorrendo.
          */
          flex: 'none',
          width: layout.settingsColumnWidth + 8,
          // Negativo anche a destra: così la colonna ne occupa comunque 900 nella fila, e i 48 px
          // fino alla scheda che spiega restano quelli del prototipo.
          margin: '-4px -4px 0 -4px',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          padding: '12px 16px 80px',
          display: 'flex',
          flexDirection: 'column',
          /*
            Il passo fra le righe è **12 e non 10**.

            Con 10, fra l'anello di una riga a fuoco e la riga successiva restava poco più di un
            pixel: i due bordi si toccavano, e a schermo si leggeva come un contorno sbagliato invece
            che come due elementi troppo vicini.

            È l'**unico** scostamento voluto dal prototipo in questa schermata, e sta scritto qui
            perché non sembri una svista: là il passo è 10, e con 10 l'anello tocca. Due pixel.
          */
          gap: 12,
        }}
      >
        <h1 style={{ font: `800 64px ${FONT_STACK}`, letterSpacing: '-.02em', margin: '0 0 20px' }}>
          {translator.t('settings.title')}
        </h1>

        {rows.map((row, index) => {
          const focused = index === cursor;
          // L'intestazione si stampa quando il gruppo cambia: non c'è un secondo elenco da tenere
          // allineato a questo, e aggiungere una riga non richiede di ricordarsi di nulla.
          const firstOfGroup = index === 0 || rows[index - 1]?.group !== row.group;
          const value = valueOf(row, settings, translator, favoritesCount, pinSet);
          return (
            /*
              Un frammento, non un contenitore con `display: contents`.

              Sarebbe stato più diretto, e non funziona dove deve: Tizen 5 monta Chromium 63, dove
              `display: contents` non esiste — l'intestazione e la riga si troverebbero dentro una
              scatola in più, e la colonna perderebbe il suo `gap`. Non è un difetto che si vede in
              sviluppo: si vede sul televisore del cliente.
            */
            <Fragment key={rowKeyOf(row)}>
              {firstOfGroup && (
                <div
                  data-settings-group={row.group}
                  style={{
                    font: `600 20px ${FONT_STACK}`,
                    color: colors.textDim,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    // Il primo gruppo è attaccato al titolo, gli altri hanno 22 px di respiro sopra:
                    // è quello che li fa leggere come sezioni invece che come righe in grassetto.
                    paddingTop: index === 0 ? 0 : 22,
                  }}
                >
                  {translator.t(`settings.group.${row.group}`)}
                </div>
              )}
              <button
                type="button"
                data-f="1"
                data-row={row.labelKey}
                data-focused={focused ? '1' : '0'}
                onClick={() => {
                  setCursor(index);
                  // `index`, non `cursor`: vedi `activate`. Il fuoco si sposta *e* si agisce sulla
                  // riga cliccata, che col puntatore sono due cose diverse.
                  // Il clic è come OK: una direzione sola, quindi avvolge. Vedi `Edge`.
                  activate(1, 'wrap', index);
                }}
                style={{
                  // Misure del prototipo: alta 84, respiro 28, angoli 14, fondo pieno sempre —
                  // non solo sulla riga a fuoco. Righe trasparenti che si accendono una alla volta
                  // fanno sembrare le altre disattivate.
                  flex: 'none',
                  height: 84,
                  padding: '0 28px',
                  border: 0,
                  margin: 0,
                  borderRadius: 14,
                  textAlign: 'left',
                  cursor: 'pointer',
                  background: colors.surface,
                  color: colors.text,
                  font: `600 27px ${FONT_STACK}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  outline: focused ? `4px solid ${colors.accent}` : 'none',
                  outlineOffset: 4,
                  // Qui il prototipo ingrandisce dell'1,02 e non dell'1,04: la riga è larga 900 px,
                  // e il 4% la farebbe sbordare oltre la colonna.
                  transform: focused ? `scale(var(${cssVariables.focusScaleSubtle}, 1.02))` : 'none',
                  transition: 'var(--tr, transform .16s ease)',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {translator.t(row.labelKey)}
                </span>
                {/*
                  Il valore in una **pastiglia**, non in testo grigio a destra.

                  È la differenza fra leggere «Animazioni … Attive» e vedere che quel valore si può
                  cambiare: la pastiglia dice «questo è un comando», e su un televisore, dove non c'è
                  un puntatore che cambia forma, è l'unico indizio disponibile.
                */}
                {value.length > 0 && (
                  <span
                    data-value="1"
                    data-on={valueIsOn(row, settings) ? '1' : '0'}
                    style={{
                      flex: 'none',
                      padding: '8px 18px',
                      borderRadius: 10,
                      /*
                        **La pastiglia si colora.** Nel prototipo è ambra con testo scuro quando la
                        voce è attiva, `#2A3342` con testo tenue quando è spenta — `vBg`/`vFg`. Erano
                        tutte dello stesso grigio, e la differenza non è estetica: su nove righe, il
                        colore è ciò che fa leggere lo stato **senza leggere le parole**. Con un
                        colore solo bisogna fermarsi su ogni riga e decifrare «Attivo» da «Disattivo»,
                        che a tre metri da un televisore sono la stessa macchia chiara.
                      */
                      background: valueIsOn(row, settings) ? colors.accent : colors.border,
                      color: valueIsOn(row, settings) ? colors.background : colors.textMuted,
                      font: `700 22px ${FONT_STACK}`,
                    }}
                  >
                    {value}
                  </span>
                )}
              </button>
            </Fragment>
          );
        })}
      </div>

      {/*
        La colonna di destra: la scheda che spiega la voce a fuoco, e sotto i dati dell'apparecchio.

        Il `paddingTop: 136` la allinea alla prima riga e non al titolo — nel prototipo la scheda
        comincia dove comincia l'elenco, perché è di quello che parla.
      */}
      <div style={{ flex: 1, minWidth: 0, paddingTop: 136, display: 'flex', flexDirection: 'column', gap: 28 }}>
        <div
          data-settings-help="1"
          style={{
            background: colors.surface,
            borderRadius: 20,
            padding: 36,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {focusedRow && (
            <>
              <div style={{ font: `700 30px ${FONT_STACK}` }}>{translator.t(focusedRow.labelKey)}</div>
              <div style={{ font: `400 24px/1.45 ${FONT_STACK}`, color: colors.textMuted }}>
                {translator.t(`${focusedRow.labelKey}.help`)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Se la pastiglia del valore va **accesa**, cioè ambra.
 *
 * Le regole sono quelle del prototipo (`vBg`), e la seconda è quella che non si indovina:
 *
 * - un **interruttore** è acceso quando è acceso;
 * - una **scelta** è sempre accesa. Non descrive uno stato acceso/spento: «Media» non è meno attivo
 *   di «Alta», e spegnere la pastiglia su un valore intermedio direbbe che quella voce non è in
 *   vigore. L'ambra qui vuol dire «questo valore è quello che sta funzionando adesso»;
 * - le **sole in lettura** e le **azioni** non sono mai accese: sono un dato o un comando, non uno
 *   stato che si può avere acceso.
 */
function valueIsOn(row: Row, settings: PlayerSettings): boolean {
  if (row.kind === 'toggle') return settings[row.key] === true;
  return row.kind === 'choice';
}

/** La chiave di React per una riga. Le azioni e le sole in lettura non hanno `key` di impostazione. */
function rowKeyOf(row: GroupedRow): string {
  return row.kind === 'readonly' || row.kind === 'action' ? row.labelKey : row.key;
}

function valueOf(
  row: Row,
  settings: PlayerSettings,
  translator: { t: (key: string, params?: Readonly<Record<string, string | number>>) => string },
  favoritesCount: number,
  pinSet: boolean,
): string {
  if (row.kind === 'readonly') return row.value;
  if (row.kind === 'action') {
    // «Canali preferiti» porta il conto a destra, come nel prototipo; «Esci» non ha valore da
    // mostrare, e la pastiglia non si disegna affatto invece di disegnarsi vuota.
    if (row.id === 'favorites') return String(favoritesCount);
    // «Attivo» o «Non impostato»: è lo stato vero, cioè se un PIN c'è — non una levetta.
    if (row.id === 'adultPin') return translator.t(pinSet ? 'settings.adultPin.on' : 'settings.adultPin.off');
    return '';
  }
  if (row.kind === 'toggle') {
    return translator.t(settings[row.key] ? 'settings.on' : 'settings.off');
  }
  const value: string | number = settings[row.key];
  if (row.key === 'locale') {
    // Il nome della lingua nella lingua stessa, e `auto` come frase tradotta: vedi `LANGUAGE_NAMES`.
    return value === 'auto' ? translator.t('settings.language.auto') : (LANGUAGE_NAMES[value as string] ?? String(value));
  }
  return row.key === 'imageQuality'
    ? translator.t(`settings.quality.${value}`)
    // Il numero entra nella frase: «3 ore» e «24 ore» non si dicono uguale in tutte le lingue, e
    // una chiave senza parametro costringerebbe a concatenare fuori dall'i18n.
    : translator.t('settings.hours', { count: value as number });
}
